// ─── Operator draft persistence (2026-09-06) ─────────────────────────────────
//
// An edited reply used to live only in AveryCard's `editedReply` state, and the
// per-ticket reset effect re-seeded that state from `ticket.draftReply`. Its
// dependency array lists `ticket.draftReply`, so the wipe was not limited to
// switching tickets: ANY new draft for the same ticket — a re-analysis, or the
// 120-second inbox poll returning a fresh object — discarded whatever the
// operator had typed, mid-composition, with no warning and no undo.
//
// That is worst exactly when the work is most careful. A rep composing a delay
// apology, or someone covering another person's queue who wants a colleague to
// review the wording before it goes, loses the draft by doing the ordinary
// thing: looking at another ticket and coming back.
//
// So drafts are persisted per thread, keyed by thread id, in localStorage —
// the same mechanism localStatus.ts uses to make status survive the poll, and
// panelPrefs.ts uses for panel state. All storage access is wrapped: a
// storage-blocked webview or private window must degrade to in-memory
// behaviour, never throw.
//
// Two properties this deliberately keeps:
//
//   * A saved draft is the operator's own words, so it WINS over Avery's. The
//     card surfaces when Avery's current draft differs and offers a one-click
//     discard, rather than silently choosing for them either way.
//   * A draft is cleared once it has actually been sent. A stale draft
//     resurrecting on a reopened ticket would be worse than losing it.
//
// Entries carry `updatedAt` and are pruned past DRAFT_TTL_MS on every read.
// These bodies contain customer names, plates and amounts, so they should not
// linger on a shared workstation indefinitely; sign-out clears the key
// outright (see AuthContext.signOut).

export const LS_DRAFTS_KEY = 'parkify_support_drafts'

/** Drafts older than this are dropped on the next read. Long enough to survive
 *  a shift and a lunch break, short enough that abandoned customer text does
 *  not accumulate on the machine. */
export const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000

export interface StoredDraft {
  /** What the operator typed. */
  text: string
  /** Avery's draft at the moment they started editing, so the card can tell
   *  "you changed this" apart from "Avery has since produced something new". */
  basedOn: string
  updatedAt: string
}

function readAll(): Record<string, StoredDraft> {
  let parsed: unknown
  try {
    parsed = JSON.parse(localStorage.getItem(LS_DRAFTS_KEY) ?? '{}')
  } catch {
    return {}
  }
  if (!parsed || typeof parsed !== 'object') return {}
  const now = Date.now()
  const out: Record<string, StoredDraft> = {}
  for (const [id, v] of Object.entries(parsed as Record<string, unknown>)) {
    if (!v || typeof v !== 'object') continue
    const d = v as Partial<StoredDraft>
    if (typeof d.text !== 'string' || typeof d.updatedAt !== 'string') continue
    const age = now - new Date(d.updatedAt).getTime()
    // NaN age (corrupt timestamp) is treated as expired rather than kept
    // forever — a draft we cannot date is a draft we cannot prune later.
    if (!Number.isFinite(age) || age > DRAFT_TTL_MS) continue
    out[id] = { text: d.text, basedOn: typeof d.basedOn === 'string' ? d.basedOn : '', updatedAt: d.updatedAt }
  }
  return out
}

function writeAll(all: Record<string, StoredDraft>): void {
  try {
    localStorage.setItem(LS_DRAFTS_KEY, JSON.stringify(all))
  } catch { /* storage full or private mode — non-fatal, draft stays in memory */ }
}

/** The operator's saved draft for this thread, or undefined. */
export function getDraft(threadId: string): StoredDraft | undefined {
  if (!threadId) return undefined
  return readAll()[threadId]
}

/**
 * Persist the operator's draft for a thread.
 *
 * Saving text that is byte-identical to `basedOn` CLEARS the entry instead of
 * storing it: an untouched draft is not an edit, and keeping one would pin the
 * ticket to a stale Avery draft forever, quietly suppressing re-analysis.
 */
export function saveDraft(threadId: string, text: string, basedOn: string): void {
  if (!threadId) return
  const all = readAll()
  if (text === basedOn) {
    if (!(threadId in all)) return
    delete all[threadId]
  } else {
    all[threadId] = { text, basedOn, updatedAt: new Date().toISOString() }
  }
  writeAll(all)
}

/** Drop a thread's draft — call once its reply has actually been sent. */
export function clearDraft(threadId: string): void {
  if (!threadId) return
  const all = readAll()
  if (!(threadId in all)) return
  delete all[threadId]
  writeAll(all)
}

/** Remove every stored draft (sign-out). */
export function clearAllDrafts(): void {
  try { localStorage.removeItem(LS_DRAFTS_KEY) } catch { /* non-fatal */ }
}

/**
 * What the composer should show for this ticket: the operator's saved draft
 * when there is one, otherwise Avery's.
 */
export function resolveDraftText(threadId: string, averyDraft: string | undefined | null): string {
  return getDraft(threadId)?.text ?? averyDraft ?? ''
}

/**
 * True when a saved operator draft exists AND Avery's current draft is not the
 * one it was based on — i.e. the analysis moved on after the operator typed.
 * Drives the "Avery's draft has changed" notice, so neither version is
 * discarded behind the operator's back.
 */
export function averyDraftMovedOn(threadId: string, averyDraft: string | undefined | null): boolean {
  const saved = getDraft(threadId)
  if (!saved) return false
  return (averyDraft ?? '') !== saved.basedOn
}
